import type { CompiledTemplateCampaign } from "@adpilot/shared";
import { IconAlertTriangle, IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  campaignDefaultBid,
  groupOfLayer,
  isAutomatic,
  PREVIEW_GROUPS,
  type PreviewGroup,
} from "./campaign-model";
import { formatMoney } from "./fields";

interface CampaignTableProps {
  campaigns: CompiledTemplateCampaign[];
  /** Campaign key → warning shown next to the name (truncated / duplicate name). */
  warnings: Map<string, string>;
  onOpen: (campaign: CompiledTemplateCampaign) => void;
  onDelete: (campaign: CompiledTemplateCampaign) => Promise<boolean>;
}

const COLUMNS = "flex items-center gap-8 px-5";

/** Grouped campaign list (Paper TP-09); 核心 starts collapsed, the rest expanded. */
export function CampaignTable({ campaigns, warnings, onOpen, onDelete }: CampaignTableProps) {
  const { t } = useTranslation("templatePortfolio");
  const [collapsed, setCollapsed] = useState<Set<PreviewGroup>>(() => new Set(["core"]));
  const [pendingDelete, setPendingDelete] = useState<CompiledTemplateCampaign | null>(null);
  const [deleting, setDeleting] = useState(false);

  const toggle = (group: PreviewGroup) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (!next.delete(group)) next.add(group);
      return next;
    });

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    const ok = await onDelete(pendingDelete);
    setDeleting(false);
    if (ok) setPendingDelete(null);
  }

  return (
    <div className="flex shrink-0 flex-col overflow-clip rounded-[14px] border border-line bg-surface [&>*:last-child]:border-b-0">
      <div
        className={`${COLUMNS} h-11 shrink-0 border-b border-line bg-table-footer text-xs font-medium text-muted-foreground`}
      >
        <span className="min-w-0 flex-1">{t("preview.table.campaign")}</span>
        <span className="w-[72px] shrink-0">{t("preview.table.targeting")}</span>
        <span className="w-24 shrink-0 text-right">{t("preview.table.defaultBid")}</span>
        <span className="w-40 shrink-0 font-normal text-text-secondary">
          {t("preview.table.actions")}
        </span>
      </div>
      {PREVIEW_GROUPS.map((group) => {
        const rows = campaigns.filter((campaign) => groupOfLayer(campaign.layer) === group);
        if (rows.length === 0) return null;
        const open = !collapsed.has(group);
        const Chevron = open ? IconChevronDown : IconChevronRight;
        return (
          <Fragment key={group}>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => toggle(group)}
              className="flex h-10 shrink-0 items-center gap-2.5 border-b border-line bg-canvas pr-3 pl-5 text-left text-xs"
            >
              <Chevron className="size-3.5 text-muted-foreground" stroke={2} aria-hidden />
              <span className="font-semibold text-ink">{t(`preview.groups.${group}`)}</span>
              <span className="text-muted-foreground">
                {t("preview.table.count", { count: rows.length })}
              </span>
            </button>
            {open
              ? rows.map((campaign) => (
                  <CampaignRow
                    key={campaign.key}
                    campaign={campaign}
                    warning={warnings.get(campaign.key)}
                    onOpen={() => onOpen(campaign)}
                    onDelete={() => setPendingDelete(campaign)}
                  />
                ))
              : null}
          </Fragment>
        );
      })}

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && !deleting && setPendingDelete(null)}
      >
        <AlertDialogPopup className="max-w-[420px] rounded-xl">
          <AlertDialogHeader className="gap-1.5 p-5">
            <AlertDialogTitle className="text-base font-semibold leading-6 text-ink">
              {t("preview.delete.title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-5 text-text-secondary">
              {t("preview.delete.description", { name: pendingDelete?.name ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="bg-canvas px-5 py-3">
            <AlertDialogClose render={<Button variant="outline" disabled={deleting} />}>
              {t("preview.delete.cancel")}
            </AlertDialogClose>
            <Button variant="destructive" loading={deleting} onClick={() => void confirmDelete()}>
              {t("preview.delete.confirm")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </div>
  );
}

function CampaignRow({
  campaign,
  warning,
  onOpen,
  onDelete,
}: {
  campaign: CompiledTemplateCampaign;
  warning: string | undefined;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation("templatePortfolio");
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: whole-row click is a mouse shortcut for 详情
    // biome-ignore lint/a11y/useKeyWithClickEvents: 详情 button is the accessible control
    <div
      onClick={onOpen}
      className={`${COLUMNS} h-12 shrink-0 cursor-pointer border-b border-line text-xs transition-colors hover:bg-table-row-hover`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-7">
        <span className="truncate text-ink">{campaign.name}</span>
        {warning ? (
          <Tooltip>
            <TooltipTrigger render={<span className="inline-flex shrink-0" />} aria-label={warning}>
              <IconAlertTriangle className="size-3.5 text-warning-foreground" aria-hidden />
            </TooltipTrigger>
            <TooltipContent>{warning}</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      <span className="w-[72px] shrink-0 text-text-secondary">
        {isAutomatic(campaign) ? t("preview.table.auto") : t("preview.table.manual")}
      </span>
      <span className="w-24 shrink-0 text-right font-mono text-ink tabular-nums">
        {formatMoney(campaignDefaultBid(campaign))}
      </span>
      <div className="flex w-40 shrink-0 items-center gap-5">
        <button
          type="button"
          className="font-medium text-primary hover:underline"
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
        >
          {t("preview.table.detail")}
        </button>
        <button
          type="button"
          className="text-destructive hover:underline"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
        >
          {t("preview.table.delete")}
        </button>
      </div>
    </div>
  );
}
